export type SchedulePreviewCadence = 'OFF' | 'DAILY' | 'WEEKLY';

export interface SchedulePreviewInput {
  enabled: boolean;
  cadence: SchedulePreviewCadence;
  dayOfWeek: number;
  executionTime: string;
  timezone: string;
  windowSeconds: number;
}

export interface SchedulePreviewResult {
  occurrenceAtUtc: string;
  windowStartUtc: string;
  windowEndUtc: string;
  occurrenceLabel: string;
}

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function dateParts(date: Date, timeZone: string): DateParts {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date);
  return Object.fromEntries(
    parts
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  ) as DateParts;
}

function parseExecutionTime(value: string): { hour: number; minute: number; second: number } {
  const match = String(value || '').trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) throw new Error('execution_time_local must be HH:MM or HH:MM:SS');
  return {
    hour: Number(match[1]),
    minute: Number(match[2]),
    second: Number(match[3] || 0),
  };
}

function mondayZeroWeekday(year: number, month: number, day: number): number {
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

function compareCivilParts(
  left: DateParts,
  right: DateParts,
): number {
  const keys: Array<keyof DateParts> = ['year', 'month', 'day', 'hour', 'minute', 'second'];
  for (const key of keys) {
    if (left[key] < right[key]) return -1;
    if (left[key] > right[key]) return 1;
  }
  return 0;
}

function zonedLocalToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): Date {
  const targetParts: DateParts = { year, month, day, hour, minute, second };
  const target = Date.UTC(year, month - 1, day, hour, minute, second);
  let guess = new Date(target);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const shown = dateParts(guess, timeZone);
    const shownAsUtc = Date.UTC(
      shown.year,
      shown.month - 1,
      shown.day,
      shown.hour,
      shown.minute,
      shown.second,
    );
    guess = new Date(guess.getTime() + target - shownAsUtc);
  }
  const resolved = dateParts(guess, timeZone);
  if (compareCivilParts(resolved, targetParts) === 0) return guess;
  for (let step = 0; step < 3 * 60 * 60; step += 1) {
    guess = new Date(guess.getTime() + 1000);
    const probe = dateParts(guess, timeZone);
    if (compareCivilParts(probe, targetParts) >= 0) return guess;
  }
  return guess;
}

function addDaysCivil(parts: Pick<DateParts, 'year' | 'month' | 'day'>, days: number): Pick<DateParts, 'year' | 'month' | 'day'> {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

export function computeNextSchedulePreview(
  input: SchedulePreviewInput,
  after: Date = new Date(),
): SchedulePreviewResult | null {
  if (!input.enabled) return null;
  const timeZone = String(input.timezone || '').trim();
  if (!timeZone) throw new Error('timezone is required');
  const { hour, minute, second } = parseExecutionTime(input.executionTime);
  const afterParts = dateParts(after, timeZone);
  let candidateDay = {
    year: afterParts.year,
    month: afterParts.month,
    day: afterParts.day,
  };
  if (input.cadence === 'WEEKLY') {
    const currentWeekday = mondayZeroWeekday(
      candidateDay.year,
      candidateDay.month,
      candidateDay.day,
    );
    const delta = (input.dayOfWeek - currentWeekday + 7) % 7;
    candidateDay = addDaysCivil(candidateDay, delta);
  }

  for (let attempt = 0; attempt < 3660; attempt += 1) {
    const occurrence = zonedLocalToUtc(
      candidateDay.year,
      candidateDay.month,
      candidateDay.day,
      hour,
      minute,
      second,
      timeZone,
    );
    if (occurrence.getTime() > after.getTime()) {
      const startLocalUtc = Date.UTC(
        candidateDay.year,
        candidateDay.month - 1,
        candidateDay.day,
        hour,
        minute,
        second,
      ) - input.windowSeconds * 1000;
      const startLocalDate = new Date(startLocalUtc);
      const windowStart = zonedLocalToUtc(
        startLocalDate.getUTCFullYear(),
        startLocalDate.getUTCMonth() + 1,
        startLocalDate.getUTCDate(),
        startLocalDate.getUTCHours(),
        startLocalDate.getUTCMinutes(),
        startLocalDate.getUTCSeconds(),
        timeZone,
      );
      return {
        occurrenceAtUtc: occurrence.toISOString(),
        windowStartUtc: windowStart.toISOString(),
        windowEndUtc: occurrence.toISOString(),
        occurrenceLabel: new Intl.DateTimeFormat(undefined, {
          timeZone,
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
        }).format(occurrence),
      };
    }
    candidateDay = addDaysCivil(
      candidateDay,
      input.cadence === 'WEEKLY' ? 7 : 1,
    );
  }
  throw new Error('unable to compute next occurrence');
}

export function nextSchedulePreviewText(
  input: SchedulePreviewInput,
  after?: Date,
): string {
  try {
    const preview = computeNextSchedulePreview(input, after);
    if (!preview) return 'Schedule is off';
    return `${preview.occurrenceLabel} (server-confirmed after save)`;
  } catch {
    return 'Enter a valid IANA timezone';
  }
}
